/**
 * The draft as the canvas draws it: `first`, the steps and the decisions as
 * nodes, each link as an edge, and each write-back as an arrow back to its
 * target. A refused save's problems land on the node or edge they name.
 *
 * A draft with no first capability has no `first` node yet (Feature 80 D2).
 *
 * A decision (Feature 79) is a gate node: a diamond whose way in is a plain
 * line from the node it follows, tied to one condition node per branch in
 * the order tried, the else last (revision 1, D26). A link out of it leaves
 * its branch's condition node and carries no gate marker.
 */
import type {
  LayoutPoint,
  LinkageBranch,
  LinkageDecision,
  LinkageDecisionMode,
  LinkageMode,
  LinkageProblem,
} from '@optum/api-client/types';

import { viewerTimeZone } from '../../lib/cron.js';
import { cronReading } from '../../lib/schedule.js';
import { conditionId, conditionsOf, type ConditionKind } from './conditions.js';
import {
  FIRST,
  linkId,
  positions,
  problemsByTarget,
  type Draft,
  type FlowEdge,
  type FlowNode,
  type ReturnEdge,
} from './draft.js';
import { conditionWords } from './gate-lines.js';
import { hasFirst } from './first.js';
import { gateOf } from './gates.js';
import { isCall, isSchedule } from './start.js';

// How far below the lowest node's top a write-back's arrow runs: past the
// tallest node the canvases draw.
const FLOOR_BELOW = 130;

/** A gate node as the canvas draws it. */
export interface GateFlowNode {
  id: string;
  position: LayoutPoint;
  data: {
    name: string;
    mode: LinkageDecisionMode;
    /** Whether it follows a node yet: a gate from the toolbar may not (D29). */
    follows: boolean;
    problems: LinkageProblem[];
  };
}

/** A condition node as the canvas draws it: one way out of its gate. */
export interface ConditionFlowNode {
  id: string;
  position: LayoutPoint;
  data: {
    gate: string;
    /** Its branch's name; `else` for the else and a switch's default. */
    branch: string;
    kind: ConditionKind;
    number: number | null;
    /** What it tests, short: its first condition, a case's values, or what the else catches. */
    words: string;
    /** An if with no condition, or a case with no value, yet. */
    empty: boolean;
  };
}

/** A gate's tie to one of its condition nodes: drawn, never a link. */
export interface Tie {
  id: string;
  source: string;
  target: string;
}

/** A decision's way in: a plain line from the node it follows. */
export interface WayIn {
  id: string;
  source: string;
  target: string;
}

/** What a condition node says it tests, short, and whether it tests nothing yet. */
export function conditionSummary(
  decision: LinkageDecision,
  branch: LinkageBranch | undefined,
): { words: string; empty: boolean } {
  if (!branch) {
    const words = decision.mode === 'switch' ? 'when no case matches' : 'when nothing above holds';
    return { words, empty: false };
  }
  if (decision.mode === 'switch') {
    const values = branch.values ?? [];
    return { words: values.join(', ') || 'no value yet', empty: values.length === 0 };
  }
  const [lead, ...rest] = conditionWords(branch.rules ?? { join: 'all', items: [] });
  if (lead === undefined) return { words: 'no condition yet', empty: true };
  const more =
    rest.length > 0 ? ` ${branch.rules?.join === 'any' ? 'or' : 'and'} ${rest.length} more` : '';
  return { words: `${lead}${more}`, empty: false };
}

/** The id of a decision's way in; never a link's `from→to`, which a problem may name. */
export function wayInId(decision: string): string {
  return `into ${decision}`;
}

export function toFlow(
  draft: Draft,
  problems: LinkageProblem[] = [],
): {
  nodes: FlowNode[];
  gates: GateFlowNode[];
  conditions: ConditionFlowNode[];
  ways: WayIn[];
  ties: Tie[];
  edges: FlowEdge[];
  returns: ReturnEdge[];
} {
  const where = problemsByTarget(problems);
  const at = positions(draft);
  const incoming = (name: string) => draft.links.filter((l) => l.to === name).length;
  const firstNode: FlowNode[] = [
    {
      id: FIRST,
      position: at[FIRST] as LayoutPoint,
      data: {
        name: FIRST,
        capabilityId: draft.firstCapabilityId,
        first: true,
        merge: null,
        schedule: isSchedule(draft.start)
          ? {
              cron: draft.start.cron ?? '',
              reading: cronReading(draft.start.cron ?? '', viewerTimeZone()),
            }
          : undefined,
        call: isCall(draft.start),
        problems: where.get(FIRST) ?? [],
        mode: 'merged' as LinkageMode,
        target: null,
      },
    },
  ];
  const nodes: FlowNode[] = [
    ...(hasFirst(draft) ? firstNode : []),
    ...draft.steps.map((step) => ({
      id: step.name,
      position: at[step.name] as LayoutPoint,
      data: {
        name: step.name,
        capabilityId: step.capability_id,
        first: false,
        merge: incoming(step.name) > 1 ? step.merge : null,
        problems: where.get(step.name) ?? [],
        mode: step.mode ?? 'merged',
        target: step.write_back?.target ?? null,
      },
    })),
  ];
  const gates: GateFlowNode[] = draft.decisions.map((d) => ({
    id: d.name,
    position: at[d.name] as LayoutPoint,
    data: {
      name: d.name,
      mode: d.mode,
      follows: d.from !== '',
      problems: where.get(d.name) ?? [],
    },
  }));
  const conditions: ConditionFlowNode[] = draft.decisions.flatMap((d) =>
    conditionsOf(d).map((c) => ({
      id: c.id,
      position: at[c.id] as LayoutPoint,
      data: {
        gate: d.name,
        branch: c.branch,
        kind: c.kind,
        number: c.number,
        ...conditionSummary(
          d,
          d.branches.find((b) => b.name === c.branch),
        ),
      },
    })),
  );
  const ways: WayIn[] = draft.decisions
    .filter((d) => d.from !== '')
    .map((d) => ({ id: wayInId(d.name), source: d.from, target: d.name }));
  const ties: Tie[] = draft.decisions.flatMap((d) =>
    conditionsOf(d).map((c) => ({ id: `tie ${c.id}`, source: d.name, target: c.id })),
  );
  const floor = Math.max(...Object.values(at).map((p) => p.y)) + FLOOR_BELOW;
  const returns: ReturnEdge[] = draft.steps.flatMap((step) =>
    step.write_back
      ? [
          {
            id: `${step.name}↩${step.write_back.target}`,
            source: step.name,
            target: step.write_back.target,
            data: { writes: step.write_back.fields, floor },
          },
        ]
      : [],
  );
  // A link out of a gate leaves its branch's condition node; its id stays the
  // agent's `from→to`, which a refused save's problem names.
  const edges: FlowEdge[] = draft.links.map((link) => ({
    id: linkId(link.from, link.to),
    source: link.branch === undefined ? link.from : conditionId(link.from, link.branch),
    target: link.to,
    data: {
      rules: link.rules,
      problems: where.get(linkId(link.from, link.to)) ?? [],
      gate: gateOf(link),
      branch: link.from === FIRST && isCall(draft.start),
    },
  }));
  return { nodes, gates, conditions, ways, ties, edges, returns };
}
